import Bugsnag from '@bugsnag/js'
import { api } from '@/store/helpers'
import { version } from '../../../../package.json'
import Application from 'App/Foundation/Application'
import { getTokenExpiry, saveToStorage } from 'App/Helpers'
import {
	ADOPT_SESSION,
	LOGOUT,
	SET_SESSION,
	SET_USER,
	SET_VERSION,
	RESET_ALERT_PREFERENCES,
} from '@/store/mutation-types'
import util from '@/util'
import {
	LOGIN_EXPIRY_DURATION_MS,
	LOGIN_TIMESTAMP_KEY,
} from '@/config/constants'

function getPartnerIdFromUrl(router) {
	const fromRouter = parseInt(router?.currentRoute?.params?.partner, 10)
	if (!Number.isNaN(fromRouter)) return fromRouter

	const match = window.location.pathname.match(/\/partners\/(\d+)/)
	return match ? parseInt(match[1], 10) : NaN
}

/**
 * The token refresh in progress in this tab, shared by every caller.
 *
 * @type {Promise|null}
 */
let pendingRefresh = null

/**
 * Get the refresh token saved in local storage. Another tab may have refreshed
 * since this tab loaded, and refresh tokens are single use, so the copy in this
 * tab's memory may already be spent.
 *
 * @return {String|null}
 */
function getStoredRefreshToken() {
	return getStoredSession()?.refresh_token || null
}

/**
 * Get the session saved in local storage, which every tab shares.
 *
 * @return {Object|null}
 */
function getStoredSession() {
	try {
		return JSON.parse(localStorage.getItem('session'))
	} catch (e) {
		return null
	}
}

/**
 * Only adopt another tab's session when it is good for at least this long.
 *
 * @type {Number}
 */
const ADOPT_MIN_REMAINING_MS = 60 * 1000

/**
 * Adopt the session another tab saved, if it differs from the given access
 * token and has not expired. Refresh tokens are single use, so when another
 * tab has already refreshed, its result is the only valid session.
 *
 * @param {Function} commit
 * @param {String} staleAccessToken
 * @return {boolean}
 */
function adoptStoredSession(commit, staleAccessToken) {
	const stored = getStoredSession()

	if (
		!stored?.access_token ||
		!stored.cached_at ||
		stored.access_token === staleAccessToken ||
		getTokenExpiry(stored) - Date.now() < ADOPT_MIN_REMAINING_MS
	) {
		return false
	}

	commit(ADOPT_SESSION, stored)
	return true
}

/**
 * Run a token refresh while holding a lock shared by every OMD tab, so only
 * one tab spends the refresh token at a time. Falls back to no lock where the
 * Web Locks API is unavailable.
 *
 * @param {Function} task
 * @return {Promise}
 */
function withRefreshLock(task) {
	if (typeof navigator !== 'undefined' && navigator.locks?.request) {
		return navigator.locks.request('omd-token-refresh', task)
	}

	return task()
}

/**
 * The forced logout already in progress, shared by concurrent callers.
 *
 * @type {Promise|null}
 */
let endingSession = null

/**
 * Report a forced logout. Bugsnag may be unstarted in tests.
 *
 * @param {String} trigger
 * @return {void}
 */
function notifyForcedLogout(trigger) {
	if (!Bugsnag._client) {
		return
	}

	Bugsnag.notify(new Error('Forced logout'), event => {
		event.severity = 'info'
		event.addMetadata('logout', { trigger })
	})
}

/**
 * The module's dispatchable actions
 *
 * @author Alejandro Sanchez <asanchez@claruscare.com>
 */
export default {
	/**
	 * Check & get the current user's authentication status and data.
	 *
	 * @param {Function} options.dispatch
	 * @param {Object} options.state
	 * @return {Promise|Boolean}
	 */
	async check({ dispatch, state }) {
		if (!state.session.access_token || !state.user.id) {
			return false
		}

		const sessionValid = await dispatch('checkSessionExpiry')
		if (!sessionValid) {
			return false
		}

		const refreshToken = state?.session?.refresh_token

		const status = await dispatch('checkOnce')
		if (status === 200) return true
		if (status !== 401) return false

		const refreshed = await dispatch('refreshAuthenticationInfo', {
			refresh_token: refreshToken,
		})
		if (!refreshed) return false

		const retryStatus = await dispatch('checkOnce')
		if (retryStatus === 200) {
			return true
		}

		await dispatch('endSession', 'refresh failed')
		return false
	},

	/**
	 * Check if the login session has exceeded 15 days; if so, end the session.
	 *
	 * @param {Function} options.dispatch
	 * @return {Promise<boolean>} false if session expired and user was logged out, true otherwise
	 */
	async checkSessionExpiry({ dispatch }) {
		const loginTimestamp = localStorage.getItem(LOGIN_TIMESTAMP_KEY)
		if (loginTimestamp) {
			const parsed = Number(JSON.parse(loginTimestamp))
			if (
				!Number.isNaN(parsed) &&
				Date.now() - parsed > LOGIN_EXPIRY_DURATION_MS
			) {
				await dispatch('endSession', '15-day cap')
				return false
			}
		}
		return true
	},

	/**
	 * Refresh when the access token is already expired, then continue.
	 * A failed refresh ends the session instead of applying stale state.
	 *
	 * @param {Object} options.state
	 * @param {Function} options.dispatch
	 * @param {String} [trigger]
	 * @return {Promise<boolean>}
	 */
	async ensureLiveSession(
		{ state, dispatch },
		trigger = 'socket event on an expired session'
	) {
		const session = state.session || {}

		if (!session.access_token) {
			await dispatch('endSession', trigger)
			return false
		}

		if (getTokenExpiry(session) > Date.now()) {
			return true
		}

		const refreshed = await dispatch('refreshAuthenticationInfo')
		if (refreshed) {
			return true
		}

		await dispatch('endSession', trigger)
		return false
	},

	/**
	 * Log the user out, report why, and show the login page.
	 * Concurrent callers share one logout.
	 *
	 * @param {Function} options.commit
	 * @param {String} [trigger]
	 * @return {Promise<void>}
	 */
	endSession({ commit, state = {} }, trigger = '401') {
		if (endingSession) {
			return endingSession
		}

		// Another tab may hold a newer session. Logging out here would delete
		// it from local storage for every tab.
		if (
			trigger !== '15-day cap' &&
			adoptStoredSession(commit, state.session?.access_token)
		) {
			return Promise.resolve()
		}

		endingSession = (async () => {
			notifyForcedLogout(trigger)
			commit(LOGOUT)
			commit(`settings/${RESET_ALERT_PREFERENCES}`, null, { root: true })

			const router = Application.getInstance()?.router
			if (!router || router.currentRoute?.name === 'login') {
				return
			}

			try {
				await router.replace({ name: 'login' })
			} catch (error) {
				console.log('The session has expired.')
			}
		})().finally(() => {
			endingSession = null
		})

		return endingSession
	},

	/**
	 * Fetch current user, apply to store. Returns HTTP status (200) or error status.
	 *
	 * @param {Function} options.dispatch
	 * @return {Promise<number>}
	 */
	async checkOnce({ dispatch }) {
		try {
			const response = await api()
				.users()
				.find()
			const user = response.get('data', {})
			await dispatch('getPartnerInfo', user)
			dispatch('setUser', user)
			return response.getStatusCode()
		} catch (err) {
			return err?.status || 500
		}
	},

	/**
	 * Attempt to authenticate a user with the given credentials.
	 *
	 * @param {Function} options.commit
	 * @param {Function} options.dispatch
	 * @param {Object} payload.username
	 * @param {Object} payload.password
	 * @return {Promise}
	 */
	async login({ commit, dispatch }, { username, password }) {
		const response = await api()
			.auth()
			.login(username, password)

		const {
			access_token,
			expires_in,
			refresh_token,
			token_type,
		} = response.get('data', {})

		const session = {
			access_token,
			expires_in,
			refresh_token,
			token_type,
		}

		await commit(SET_SESSION, session)

		saveToStorage(LOGIN_TIMESTAMP_KEY, Date.now())

		const userData = await api()
			.users()
			.find()

		const user = userData.get('data', {})

		await dispatch('getPartnerInfo', user)
		commit(SET_USER, user)
		dispatch('setUser', user)

		const app = Application.getInstance()

		app.emit('auth:success', app)

		return response
	},

	/**
	 * Refresh the access token with the refresh token saved in local storage.
	 * Concurrent callers in this tab share one refresh, and tabs take turns
	 * through a shared lock. A tab that gets the lock after another tab has
	 * refreshed adopts that session instead of spending the refresh token again.
	 *
	 * @param {Function} options.commit
	 * @param {Object} options.state
	 * @param {Object} [payload]
	 * @param {String} [payload.refresh_token] - Used only if none is stored.
	 * @return {Promise<boolean>}
	 */
	refreshAuthenticationInfo({ commit, state = {} }, payload = {}) {
		if (pendingRefresh) {
			return pendingRefresh
		}

		const staleAccessToken = state.session?.access_token

		const refresh = async () => {
			if (adoptStoredSession(commit, staleAccessToken)) {
				return true
			}

			try {
				const response = await api()
					.auth()
					.refreshAccessToken({
						refresh_token:
							getStoredRefreshToken() || payload.refresh_token,
					})

				const {
					access_token,
					expires_in,
					refresh_token,
					token_type,
				} = response.get('data', {})

				const session = {
					access_token,
					expires_in,
					refresh_token,
					token_type,
				}

				await commit(SET_SESSION, session)
				return true
			} catch (error) {
				// Without the lock, another tab may have spent the token first.
				return adoptStoredSession(commit, staleAccessToken)
			}
		}

		pendingRefresh = withRefreshLock(refresh).finally(() => {
			pendingRefresh = null
		})

		return pendingRefresh
	},

	/**
	 * Logout the current user.
	 *
	 * @param {Function} options.commit
	 * @param {Object} [payload]
	 * @param {boolean} [payload.signOutCalled=false] - When true, calls auth API to invalidate session server-side.
	 * @return {Promise<void>}
	 */
	async logout({ commit }) {
		commit(LOGOUT)
		commit(`settings/${RESET_ALERT_PREFERENCES}`, null, { root: true })
	},

	async acknowledgeLogout() {
		try {
			await api()
				.auth()
				.logout()
		} catch (error) {
			console.error('User logout status not updated.')
		}
	},

	/**
	 * Set the authenticated user.
	 *
	 * @param {Function} options.commit
	 * @param {Function} options.dispatch
	 * @param {Object} model
	 * @return {void}
	 */
	async setUser({ commit, dispatch, rootState }, model) {
		const { ...user } = model

		const payload = rootState.partners?.pagination?.isSearching || false

		const partnerList = await dispatch('partners/getPartnerList', null, {
			root: true,
		})

		const newPartnerIds = partnerList
			.get('partners', [])
			.map(partner => partner.id)

		if (payload) {
			user.partner_ids = newPartnerIds
		} else {
			user.partner_ids = Array.from(
				new Set([...(user.partner_ids || []), ...newPartnerIds])
			)
		}

		user.messaging_profile_ids = []

		commit(SET_USER, user)
	},

	async getPartnerInfo({ dispatch, rootState }, user) {
		const routePartnerId = getPartnerIdFromUrl(
			Application.getInstance()?.router
		)

		if (!Number.isNaN(routePartnerId)) {
			try {
				await dispatch('partners/setActive', routePartnerId, {
					root: true,
				})
				return
			} catch (error) {
				console.error('Failed to set active partner from URL.', error)
			}
		}

		const { partners } = user

		const sortedPartners = util.sortPartners(partners || [])

		const defaultPartnerId = sortedPartners.length
			? sortedPartners[0].id
			: null

		const activePartnerId = rootState.partners?.active

		if (activePartnerId) {
			await dispatch('partners/find', activePartnerId, { root: true })
		}

		if (!activePartnerId && defaultPartnerId) {
			await dispatch('partners/setActive', defaultPartnerId, {
				root: true,
			})
		}

		if (!activePartnerId && !defaultPartnerId) {
			const page = rootState.partners.pagination.current_page || 1
			const keyword = rootState.partners.pagination.searchKeyword || ''

			const response = await api()
				.partners()
				.page(page)
				.search(keyword)
				.get()

			const sortedPartnersList = util.sortPartners(
				response.get('partners', [])
			)
			const firstPartnerId = sortedPartnersList[0]?.id || null
			await dispatch('partners/setActive', firstPartnerId, {
				root: true,
			})
		}
	},
	/**
	 * Set the current code base version.
	 *
	 * @param {Function} options.commit
	 * @param {Function} options.dispatch
	 * @return {void}
	 */
	async setVersion({ commit }) {
		commit(SET_VERSION, version)
	},

	async verifyOutboundCallerId(_, payload) {
		const { phoneNumber, extensionCode } = payload

		return api()
			.users()
			.verifyOutboundCallerId(phoneNumber, extensionCode)
	},
}
